import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ChatAPI from 'chat/ChatAPI.js';
import { transcriptKeys } from '../transcripts/queries';

// Separate hook for invalidating a specific transcript's AI coding
export function useRefreshTranscriptAICoding() {
  const queryClient = useQueryClient();
  
  return (transcriptId) => {
    if (!transcriptId) {
      console.error("Cannot invalidate AI coding - no transcript ID provided");
      return false;
    }
    queryClient.invalidateQueries({
      queryKey: ['aiCoding', transcriptId]
    });
    return true;
  };
}

export function useAICoding(transcriptId, enabled=true) {
  const queryClient = useQueryClient();

  // Helper function to extract job from response
  const getJobFromResponse = (response) => {
    if (!response || !response.ai_coding_jobs || !response.ai_coding_jobs.length) {
      return null;
    }
    return response.ai_coding_jobs.find(job => job.transcript_id === Number(transcriptId)) || response.ai_coding_jobs[0];
  };
  
  // Single source of truth - one query to determine job status
  const codingQuery = useQuery({
    queryKey: ['aiCoding', transcriptId],
    queryFn: () => ChatAPI.getAICoding(transcriptId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: enabled,
    refetchInterval: (query) => {
      const job = getJobFromResponse(query?.state?.data?.data);
      // Check if there was an error status returned from the API
      if ( !job ) return false;

      if (job?.status === 'error') {
        return false; // Stop polling if there's an error
      }

      // Stop polling once we have a completed status
      return job?.status === 'completed' || job?.status === 'done' ? false : 10000; // Poll every 10 seconds
    },
    refetchIntervalInBackground: true,
  });


  // Get current job (our single source of truth)
  const currentJob = getJobFromResponse(codingQuery.data?.data);
  
  // Determine if we have an existing job (job exists and wasn't just created)
  const hasExistingCoding = Boolean(currentJob);

  // Mutation for creating the AI coding
  const createMutation = useMutation({
    mutationFn: () => ChatAPI.createAICoding(transcriptId),
    onSuccess: () => {
      // Invalidate the query to trigger refetching
      queryClient.invalidateQueries({ queryKey: ['aiCoding', transcriptId] });
    },
  });

  // Mutation for stopping the AI coding
  const stopMutation = useMutation({
    mutationFn: () => {
      if (!currentJob?.id) {
        console.error("Cannot stop coding - no job ID available");
        return Promise.reject("No job ID available");
      }
      return ChatAPI.stopAICoding(currentJob.id);
    },
    onSuccess: () => {
      // Invalidate the query to trigger refetching
      queryClient.invalidateQueries({ queryKey: ['aiCoding', transcriptId] });
    },
  });

  // Mutation for removing the AI generated codes
  const destroyMutation = useMutation({
    mutationFn: () => {
      if (!currentJob?.id) {
        console.error("Cannot destroy coding - no job ID available");
        return Promise.reject("No job ID available");
      }
      return ChatAPI.destroyAICoding(currentJob.id);
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['aiCoding', transcriptId] });
      // Invalidate transcript to refetch excerpts
      queryClient.invalidateQueries({ queryKey: transcriptKeys.detail(transcriptId) });
      // Reset the create mutation
      createMutation.reset();
    },
  });

  return {
    // For initiating the process
    startAICoding: createMutation.mutate,
    hasStarted: createMutation.isSuccess,
    isCreating: createMutation.isPending,
    startAiCodingError: createMutation.error,
    
    // For accessing the coding data
    currentJob,
    isLoading: codingQuery.isPending && !hasExistingCoding,
    error: codingQuery.error,
    
    // Status flags - simpler status determination
    isProcessing: createMutation.isPending || (currentJob?.status === 'in_progress'),
    isComplete: currentJob?.status === 'completed' || currentJob?.status === 'done',
    jobHasFailed: (currentJob?.status === 'error'),
    
    // Information about whether we found a job
    hasExistingCoding,
    
    // Stop functionality
    stopAICoding: stopMutation.mutate,
    isStopping: stopMutation.isPending,
    stopError: stopMutation.error,

    // Destroy functionality
    destroyAICoding: destroyMutation.mutate,
    isDestroying: destroyMutation.isPending,
    hasDestroyed: destroyMutation.isSuccess,
    destroyError: destroyMutation.error,
    
    // Reset function to try again after failure
    reset: () => {
      createMutation.reset();
      destroyMutation.reset();
      stopMutation.reset();
      queryClient.removeQueries({
        queryKey: ['aiCoding', transcriptId]
      });
    }
  };
}